// L'onglet Logement : la résidence principale (l'acheter, la rembourser, la revendre) et la courbe de l'immobilier.
import { useState } from 'preact/hooks';
import { MORTGAGE, PROPERTIES, PROPERTY_KINDS, type MortgageYears } from '../../content/property';
import { round2 } from '../../engine/finance';
import { fmtDate, fmtEur, fmtPct, fmtRate } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { GameState, MortgageInsurance, PropertyKind } from '../../engine/state';
import { update } from '../../engine/store';
import { DAYS_PER_YEAR, dateAtDay } from '../../engine/time';
import { unlockPage } from '../../game/actions';
import {
  buyHome,
  canSellHome,
  homeValue,
  minimumApport,
  moneyAvailable,
  mortgageEndAge,
  mortgageLoan,
  prepayMinimum,
  prepayMortgage,
  prepayPreview,
  propertyPrice,
  purchasePlan,
  purchaseRefusal,
  salePreview,
  sellHome,
} from '../../game/property';
import { Card, Line, LineChart } from '../components';
import { formatDuration, signedPct } from '../labels';

export function HousingTab({ state }: { state: GameState }) {
  return (
    <div class="grid">
      {state.home ? <HomeCard state={state} /> : <BuyCard state={state} />}
      {state.home && mortgageLoan(state) && <PrepayCard state={state} />}
      {state.home && <SellCard state={state} />}
      <RealEstateCard state={state} />
    </div>
  );
}

/** Le simulateur d'achat : le bien, l'apport, la durée, l'assurance, et la réponse de la banque. */
function BuyCard({ state }: { state: GameState }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PropertyKind>('t2');
  const [apport, setApport] = useState<number | null>(null);
  const [years, setYears] = useState<MortgageYears>(25);
  const [insurance, setInsurance] = useState<MortgageInsurance>('deleguee');
  if (!open) {
    return (
      <Card title={t('property.buyTitle')} className="card-wide">
        <p class="muted">{t('property.buyIntro')}</p>
        <button
          class="button-primary"
          onClick={() => {
            update((s) => unlockPage(s, 'acheter_louer'));
            setOpen(true);
          }}
        >
          {t('property.simulate')}
        </button>
      </Card>
    );
  }
  const available = moneyAvailable(state);
  const min = minimumApport(state, kind);
  // Au-delà du prix et des frais, un apport ne sert plus à rien : c'est un achat sans crédit.
  const fullCost = purchasePlan(state, kind, Number.MAX_SAFE_INTEGER, years, insurance).apport;
  const sliderMax = Math.max(min, Math.min(available, fullCost));
  const chosen = Math.min(sliderMax, Math.max(min, apport ?? min));
  const plan = purchasePlan(state, kind, chosen, years, insurance);
  const refusal = purchaseRefusal(state, plan);
  return (
    <Card title={t('property.buyTitle')} className="card-wide">
      <div class="choice-grid">
        {PROPERTY_KINDS.map((id) => (
          <button key={id} class="choice-card" aria-pressed={id === kind} onClick={() => setKind(id)}>
            <strong>{t(PROPERTIES[id].title)}</strong>
            <span>{fmtEur(propertyPrice(state, id))}</span>
            <span class="muted">{t(PROPERTIES[id].desc)}</span>
          </button>
        ))}
      </div>
      <p class="muted">{t('property.apport', { amount: fmtEur(chosen) })}</p>
      <div class="slider">
        <input
          type="range"
          min={min}
          max={sliderMax}
          step={1000}
          value={chosen}
          disabled={sliderMax <= min}
          aria-label={t('property.apportLabel')}
          onInput={(event) => setApport(Number((event.target as HTMLInputElement).value))}
        />
      </div>
      <p class="hint">{t('property.apportHint', { min: fmtEur(min), available: fmtEur(available) })}</p>
      <label class="field">
        <span class="muted">{t('property.duration')}</span>
        <select value={years} onChange={(event) => setYears(Number((event.target as HTMLSelectElement).value) as MortgageYears)}>
          {MORTGAGE.durations.map((item) => (
            <option key={item} value={item}>
              {t('property.years', { count: item, rate: fmtRate(MORTGAGE.rates[item]) })}
            </option>
          ))}
        </select>
      </label>
      <label class="field">
        <span class="muted">{t('property.insurance')}</span>
        <select
          value={insurance}
          onChange={(event) => setInsurance((event.target as HTMLSelectElement).value as MortgageInsurance)}
        >
          <option value="deleguee">{t('property.insuranceDeleguee', { rate: fmtRate(MORTGAGE.insurance.deleguee) })}</option>
          <option value="banque">{t('property.insuranceBanque', { rate: fmtRate(MORTGAGE.insurance.banque) })}</option>
        </select>
      </label>
      <Line label={t('property.linePrice')} value={fmtEur(plan.price)} />
      <Line label={t('property.lineFees')} value={fmtEur(plan.notary + plan.guarantee + plan.fileFee)} />
      <p class="hint">
        {t('property.feesDetail', {
          notary: fmtEur(plan.notary),
          guarantee: fmtEur(plan.guarantee),
          file: fmtEur(plan.fileFee),
        })}
      </p>
      <Line label={t('property.lineApport')} value={fmtEur(plan.apport)} />
      <Line label={t('property.lineLoan')} value={fmtEur(plan.loan)} tone="strong" />
      {plan.loan > 0 && (
        <>
          <Line label={t('property.lineMonthly')} value={fmtEur(plan.monthly)} tone="strong" />
          <p class="hint">
            {t('property.monthlyDetail', { payment: fmtEur(plan.payment), insurance: fmtEur(plan.insuranceMonthly) })}
          </p>
          <Line label={t('property.lineCost')} value={fmtEur(plan.totalInterest + plan.totalInsurance)} />
          {Number.isFinite(plan.debtRatio) && (
            <Line
              label={t('property.lineRatio')}
              value={fmtPct(plan.debtRatio)}
              tone={plan.debtRatio > MORTGAGE.maxDebtRatio ? 'bad' : undefined}
            />
          )}
        </>
      )}
      {refusal ? (
        <p class="warning">{t(refusal.key, refusal.params)}</p>
      ) : (
        <p class="tone-good">{t('property.accepted')}</p>
      )}
      <div class="button-row">
        <button
          class="button-primary"
          disabled={refusal !== null}
          onClick={() => update((s) => buyHome(s, kind, chosen, years, insurance))}
        >
          {t('property.sign')}
        </button>
        <button onClick={() => setOpen(false)}>{t('invest.cancel')}</button>
      </div>
    </Card>
  );
}

/** La résidence principale : ce qu'elle vaut, ce qui reste dû, ce qui t'appartient vraiment, et la fin du crédit. */
function HomeCard({ state }: { state: GameState }) {
  const home = state.home;
  if (!home) return null;
  const loan = mortgageLoan(state);
  const value = homeValue(state);
  const balance = loan?.balance ?? 0;
  const interest = loan ? round2((loan.balance * loan.annualRate) / 12) : 0;
  const endAge = mortgageEndAge(state);
  return (
    <Card title={t('property.ownerTitle')} className="card-wide">
      <p class="eyebrow">{t(PROPERTIES[home.kind].title)}</p>
      <Line label={t('property.value')} value={fmtEur(value)} tone="strong" />
      <p class={value < home.price ? 'tone-bad' : 'tone-good'}>
        {t('property.valueChange', { change: signedPct(value / home.price - 1), price: fmtEur(home.price) })}
      </p>
      {loan && <Line label={t('property.balance')} value={fmtEur(-balance)} tone="bad" />}
      <Line label={t('property.equity')} value={fmtEur(value - balance)} tone="strong" />
      {loan ? (
        <>
          <Line label={t('property.monthly')} value={fmtEur(loan.monthlyPayment + (loan.insuranceMonthly ?? 0))} />
          <p class="hint">
            {t('property.monthlyNow', {
              interest: fmtEur(interest),
              capital: fmtEur(loan.monthlyPayment - interest),
              insurance: fmtEur(loan.insuranceMonthly ?? 0),
            })}
          </p>
          <p class="muted">
            {t('property.end', {
              date: fmtDate(dateAtDay(state.startDate, state.day + (loan.monthsLeft * DAYS_PER_YEAR) / 12)),
              age: endAge ?? 0,
            })}
          </p>
        </>
      ) : (
        <p class="tone-good">{t('property.paidOff')}</p>
      )}
      {state.project === 'toit' &&
        (state.flags.projetToit ? (
          <p class="tone-good">{t('property.projectDone')}</p>
        ) : endAge !== null ? (
          <p class={endAge < MORTGAGE.projectAge ? 'muted' : 'warning'}>
            {t(endAge < MORTGAGE.projectAge ? 'property.projectOnTrack' : 'property.projectLate', {
              age: endAge,
              limit: MORTGAGE.projectAge,
            })}
          </p>
        ) : null)}
    </Card>
  );
}

/** Rembourser par anticipation : ce qu'on gagne en mois et en intérêts, face à ce que rapporterait le livret. */
function PrepayCard({ state }: { state: GameState }) {
  const loan = mortgageLoan(state);
  if (!loan) return null;
  const min = prepayMinimum(state);
  const partial = prepayPreview(state, min);
  const all = prepayPreview(state, loan.balance);
  const available = moneyAvailable(state);
  return (
    <Card title={t('property.prepayTitle')}>
      <p class="muted">{t('property.prepayIntro')}</p>
      {partial && !partial.full && (
        <p>
          {t('property.prepayPreview', {
            amount: fmtEur(partial.amount),
            months: partial.monthsBefore - partial.monthsAfter,
            interest: fmtEur(partial.interestSaved + partial.insuranceSaved),
            penalty: fmtEur(partial.penalty),
            livret: fmtEur(partial.livretGain),
          })}
        </p>
      )}
      <div class="button-row">
        {partial && !partial.full && (
          <button
            disabled={available < partial.amount + partial.penalty}
            onClick={() => update((s) => prepayMortgage(s, min))}
          >
            {t('property.prepaySome', { amount: fmtEur(partial.amount + partial.penalty) })}
          </button>
        )}
        {all && (
          <button
            disabled={available < all.amount + all.penalty}
            onClick={() => update((s) => prepayMortgage(s, s.loans.find((item) => item.mortgage)?.balance ?? 0))}
          >
            {t('property.prepayAll', { amount: fmtEur(all.amount + all.penalty) })}
          </button>
        )}
      </div>
      {partial && available < partial.amount + partial.penalty && (
        <p class="hint">{t('property.prepayNeed', { amount: fmtEur(partial.amount + partial.penalty) })}</p>
      )}
    </Card>
  );
}

/** Revendre : ce qui resterait une fois le crédit soldé, et le retour en location. */
function SellCard({ state }: { state: GameState }) {
  const [confirm, setConfirm] = useState(false);
  const preview = salePreview(state);
  if (!preview) return null;
  const possible = canSellHome(state);
  return (
    <Card title={t('property.sellTitle')}>
      <p class="muted">
        {t('property.sellPreview', {
          value: fmtEur(preview.value),
          agency: fmtEur(preview.agency),
          balance: fmtEur(preview.balance),
          penalty: fmtEur(preview.penalty),
          net: fmtEur(preview.net),
          moving: fmtEur(preview.moving),
        })}
      </p>
      {!possible && <p class="warning">{t('property.sellImpossible')}</p>}
      {confirm ? (
        <div class="button-row">
          <button
            class="button-primary"
            disabled={!possible}
            onClick={() => {
              update((s) => sellHome(s));
              setConfirm(false);
            }}
          >
            {t('property.sellConfirm')}
          </button>
          <button onClick={() => setConfirm(false)}>{t('invest.cancel')}</button>
        </div>
      ) : (
        <button disabled={!possible} onClick={() => setConfirm(true)}>
          {t('property.sell')}
        </button>
      )}
    </Card>
  );
}

/** La courbe des prix de l'immobilier, et ce que coûtent les biens aujourd'hui. */
function RealEstateCard({ state }: { state: GameState }) {
  const market = state.market;
  const history = market.realEstateHistory;
  const values = [...history.slice(-120), market.realEstate];
  const duration = formatDuration(((values.length - 1) * DAYS_PER_YEAR) / 12);
  const yearAgo = history.length > 12 ? history[history.length - 13] : null;
  const change = yearAgo ? market.realEstate / yearAgo - 1 : null;
  return (
    <Card title={t('property.marketTitle')}>
      {change !== null && (
        <p class={change < 0 ? 'tone-bad' : 'tone-good'}>{t('property.marketChange', { change: signedPct(change) })}</p>
      )}
      {values.length > 1 && <LineChart values={values} label={t('property.marketChart', { duration })} />}
      <ul class="plain-list">
        {PROPERTY_KINDS.map((id) => (
          <li key={id}>
            {t('property.priceLine', { property: t(PROPERTIES[id].title), price: fmtEur(propertyPrice(state, id)) })}
          </li>
        ))}
      </ul>
      <p class="hint">{t('property.marketHint')}</p>
    </Card>
  );
}
